import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/comics';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '全部漫画', to: '/comics' },
  { label: '热门排行', to: '/comics?sort=hot' },
  { label: '新上架', to: '/comics?sort=new' },
  { label: '完结作品', to: '/comics?sort=completed' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setMenuOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background-50/90 backdrop-blur border-b border-background-200">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-article-line text-lg"></i>
            </span>
            <span className="font-display text-xl text-primary-600 whitespace-nowrap">4399漫画网</span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600 whitespace-nowrap"
              >
                {l.label}
              </Link>
            ))}
            <div className="relative">
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600 whitespace-nowrap"
                aria-expanded={catOpen}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 grid w-64 grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2">
                  {categories.map((c) => (
                    <Link
                      key={c.name}
                      to={`/comics?category=${encodeURIComponent(c.name)}`}
                      onClick={() => setCatOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                    >
                      <i className={`${c.icon} text-secondary-500`}></i>
                      {c.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 rounded-full bg-background-100 border border-background-200 pl-3 pr-1 py-1 focus-within:border-primary-300">
            <i className="ri-search-line text-foreground-500 text-sm"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索想追的漫画"
              aria-label="搜索漫画"
              className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="rounded-full bg-primary-500 px-3 py-1.5 text-xs text-background-50 hover:bg-primary-600 whitespace-nowrap">
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2 rounded-full bg-background-100 border border-background-200 px-3 py-2">
            <i className="ri-search-line text-foreground-500 text-sm"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索想追的漫画"
              aria-label="搜索漫画"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50 whitespace-nowrap">
              搜索
            </button>
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
              >
                {l.label}
              </Link>
            ))}
            <div className="px-3 pt-2">
              <p className="mb-2 text-xs font-medium text-foreground-500">分类浏览</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.name}
                    to={`/comics?category=${encodeURIComponent(c.name)}`}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}